import { useState } from 'react';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useViewport } from '../../hooks/useViewport';
import {
  ThemeConfigPanel,
  ThemePreview,
  ThemeImportExport,
  ThemeErrorNotification,
} from '../../components/theme';
import { useTheme } from '../../hooks/useTheme';
import type { ThemeServiceError } from '../../services/themeService';

export function AdminThemeSettingsPage() {
  useViewport(); // For responsive behavior
  const [activeView, setActiveView] = useState<'config' | 'preview' | 'import-export'>('config');
  const { error, isLoading } = useTheme();
  const [showError, setShowError] = useState(true);

  const views = [
    {
      id: 'config' as const,
      label: 'Configuration',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
        </svg>
      ),
    },
    {
      id: 'preview' as const,
      label: 'Preview',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      ),
    },
    {
      id: 'import-export' as const,
      label: 'Import/Export',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
          />
        </svg>
      ),
    },
  ];

  return (
    <AdminLayout>
      {error && showError && (
        <ThemeErrorNotification error={error as ThemeServiceError} onDismiss={() => setShowError(false)} />
      )}

      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-4">
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900">Theme Settings</h1>
            <p className="mt-1 text-xs sm:text-sm text-gray-500">
              Customize colors and visual appearance
            </p>
          </div>
          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-primary-600 bg-primary-50 px-3 py-1.5 rounded-full">
              <div className="w-4 h-4 border-2 border-primary-600 border-t-transparent rounded-full animate-spin" />
              <span>Loading...</span>
            </div>
          )}
        </div>

        {/* Tab Navigation - Horizontal scroll on mobile */}
        <div className="bg-white rounded-xl border border-gray-200 p-1 sm:p-1.5 shadow-sm overflow-x-auto mobile-scroll">
          <nav className="flex gap-1 min-w-max">
            {views.map((view) => (
              <button
                key={view.id}
                onClick={() => setActiveView(view.id)}
                className={`
                  flex-1 flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 text-sm font-medium rounded-lg transition-all duration-200 min-h-[44px] whitespace-nowrap
                  ${
                    activeView === view.id
                      ? 'bg-primary-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                  }
                `}
              >
                {view.icon}
                <span className="hidden sm:inline">{view.label}</span>
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
          <div className={activeView === 'preview' ? 'lg:col-span-3' : 'lg:col-span-2'}>
            {activeView === 'config' && <ThemeConfigPanel />}
            {activeView === 'import-export' && <ThemeImportExport />}
            {activeView === 'preview' && (
              <div className="bg-white rounded-xl border border-gray-200 p-4 sm:p-6 shadow-sm">
                <div className="flex items-center gap-3 mb-4 sm:mb-6">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center flex-shrink-0">
                    <svg
                      className="w-5 h-5 text-white"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                      />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-base sm:text-lg font-semibold text-gray-900">Full Theme Preview</h2>
                    <p className="text-xs sm:text-sm text-gray-500">
                      See how your changes affect the app
                    </p>
                  </div>
                </div>
                <ThemePreview />
              </div>
            )}
          </div>

          {/* Preview sidebar - hidden on mobile when not in preview mode */}
          {activeView !== 'preview' && (
            <div className="hidden lg:block lg:col-span-1">
              <div className="sticky top-6">
                <ThemePreview />
              </div>
            </div>
          )}
        </div>

        {/* Tips Card - Simplified on mobile */}
        <div className="bg-gradient-to-r from-primary-50 to-blue-50 rounded-xl border border-primary-100 p-4 sm:p-6">
          <div className="flex gap-3 sm:gap-4">
            <div className="flex-shrink-0 hidden sm:block">
              <div className="w-10 h-10 rounded-lg bg-primary-100 flex items-center justify-center">
                <svg
                  className="w-5 h-5 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                  />
                </svg>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 mb-2">💡 Quick Tips</h3>
              <ul className="text-xs sm:text-sm text-gray-600 space-y-1 sm:space-y-1.5">
                <li>• Use presets for quick theme changes</li>
                <li>• Export your theme to backup configurations</li>
                <li className="hidden sm:block">• Changes are applied in real-time</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
